<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t("pages.driver.historyList.editComment.title") }}</h1>
      </modal-header>
    </template>

    <comment-modal-form
      v-model="rawDriverComment"
      v-model:files="files"
      :loading="anyLoading"
      :errors="errors"
      @save="saveNewComment"
      @openFiles="openFiles"
    />
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { date, object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import CommentModalForm from '@pages/drivers/driver/CommentModals/CommentModalForm.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { Upload, useFileUpload } from '@utils/upload/fileUpload';
import type { Driver, DriverCommentAttachment } from '@/entities/Entities';

interface Props {
  open: boolean;
  driver: Driver;
  editDriverComment: {
    driver: Driver | null;
    attachments: DriverCommentAttachment[];
    author: null | string;
    type: null | string;
    text: null | string;
    id: string | null;
    createdAt: Date | null;
  };
}

const emit = defineEmits(['update:open', 'update:editDriverComment', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const rawDriverComment = useVModel(props, 'editDriverComment', emit);

const {
  files,
  open: openFiles,
  updateRemote,
  reset: resetFiles,
  loading: uploading,
} = useFileUpload(
  computed(
    () => props.editDriverComment?.attachments
      ?.filter((attachment) => attachment.file)
      ?.map((attachment) => Upload.from(attachment.file)) ?? [],
  ),
);

const { t } = useI18n();

const notify = useNotify();

const {
  data: driverComment,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    // author: string().nullable().required(t('validation.notEmpty')),
    type: string().nullable().required(t('validation.notEmpty')),
    text: string().nullable().required(t('validation.notEmpty')),
    createdAt: date().nullable().required(t('validation.notEmpty')),
  }),
  rawDriverComment,
);

const initialDriverComment: {
  driver: Driver | null;
  author: null | string;
  type: null | string;
  text: null | string;
  id: string | null;
  createdAt: Date | null;
} = {
  driver: props.driver,
  author: null,
  type: null,
  text: null,
  id: null,
  createdAt: null,
};

function resetData() {
  rawDriverComment.value = {
    ...initialDriverComment,
  } as unknown as typeof rawDriverComment['value'];
  resetFiles();
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const anyLoading = computed(() => loading.value || uploading.value);

const saveNewComment = validatedFn(async () => {
  try {
    const getAttachment = (fileId: string) => props.editDriverComment.attachments.find((attachment) => attachment.file.id === fileId);

    const nextFiles = await updateRemote('/files', (upload) => {
      const attachment = getAttachment(upload.file.id);
      if (!attachment) throw new Error('Attachment not found');
      return attachment['@id'];
    });

    await exec(`/driver_comments/${props.editDriverComment.id}`, {
      method: 'PUT',
      body: {
        driver: driverComment.value.driver?.['@id'],
        // author: driverComment.value.author,
        type: driverComment.value.type,
        text: driverComment.value.text,
        createdAt: driverComment.value.createdAt,
        attachments: nextFiles?.map((file) => ({
          id: getAttachment(file.file.id)?.['@id'],
          file: file.file['@id'],
        })),
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.driver.historyList.newComment.saveSuccess'),
    });

    emit('saved');
    closeModal();
    resetFiles();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.driver.historyList.newComment.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
